/*
	This mixin is used to identify in which state our form currently is :
	loading, success, failure or neutral which is represented by null.

	It is also responsible to know if the form has been modified and to
	provide a way to reset the form the it's initial value.

	This mixin also accepts a facultative saving condition to overwrite
	the default one which is if the form data has been modified.
*/

import _isEqual from 'lodash/isEqual';

export default {
	props: {
		/*
			A custom saving condition can be passed as a props of type function
			and be executed on every content change in order to determine if
			the form is can currently be saved.
		*/
		savingCondition: {
			type: Function,
			required: false,
			default: null,
		},

		state: {
			type: String,
			required: false,
			default: 'neutral',
			validator: (state) => {
				// The state props can either be nulled or one of the three supported
				const supportedStates = ['loading', 'success', 'failure', 'neutral'];
				return !state || supportedStates.indexOf(state) !== -1;
			},
		},
	},

	data() {
		return {
			initialData: null,
			lastEmittedModified: null,
			lastEmittedSavable: null,
		};
	},

	computed: {
		modified() {
			// The form is considered modified by default if the initial data
			// object is not equal to the current data object
			const modified = !_isEqual(this.data, this.initialData);

			// Emit the change in the modified status
			if (this.lastEmittedModified !== modified && this.initialData) {
				this.lastEmittedModified = modified;
				this.$emit('modified-status', modified);
			}

			return modified;
		},

		savable() {
			// The form is considered savable by default is it is modified. If provided,
			// a custom saving condition as a method can be passed throught props
			const savable = this.savingCondition ? this.savingCondition() : this.modified;

			// Emit the change in the savable status
			if (this.lastEmittedSavable !== savable && this.initialData) {
				this.lastEmittedSavable = savable;
				this.$emit('savable-status', savable);
			}

			return savable;
		},

		submitButtonState() {
			// We determine which button state we must give the submit button
			// based on the form state. This button state handle the button
			// UI and animation in the button component
			switch (this.state) {
				case 'loading':
					return 'is-loading';

				case 'success':
					return 'is-successful';

				case 'failure':
					return 'is-unsuccessful';

				case 'neutral':
				default:
					return null;
			}
		},
	},

	mounted() {
		// If there is data provided initially, we keep a cloned version
		// of the those data in order to know if they were modified
		if (this.data) {
			this.initialData = this.cloneObject(this.data);
		}
	},

	methods: {
		reset() {
			this.removeMessage();

			// We loop through each key of the initialData and assigned
			// the value to the current data variable. Otherwise, we
			// would get a an error saying we can't modify props.
			const initialKeys = Object.keys(this.initialData);

			initialKeys.forEach((key) => {
				this.data[key] = this.initialData[key];
			});

			this.$emit('reset');
		},
	},
};
